import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from './apiClient';
import PaymentMethods from './components/PaymentMethods';
import PermissionDeniedModal from './components/PermissionDeniedModal';
import EmailChangeModal from './components/EmailChangeModal';
import NotificationSettings from './pages/NotificationSettings';

const componentTranslations = {
    ja: {
        mainTitle: "お客様情報",
        editButton: "編集",
        cancelButton: "キャンセル",
        saveButton: "保存",
        changePasswordButton: "ログインパスワード変更",
        passwordModalTitle: "パスワード変更",
        newPasswordLabel: "新しいパスワード",
        passwordConfirmationLabel: "新しいパスワード（確認用）",
        passwordMismatchError: "パスワードが一致しません。",
        passwordSuccessMessage: "パスワードが正常に変更されました。",
        passwordErrorMessage: "パスワードの変更に失敗しました。パスワードは8文字以上で設定してください。",
        profileUpdateSuccess: "プロフィールを更新しました。",
        profileUpdateError: "プロフィールの更新に失敗しました。",
        fetchProfileError: "プロフィールの取得に失敗しました。",
        notLoggedIn: "ログインしていません。",
        loadingProfile: "お客様の情報を読み込んでいます...",
        profileNotFound: "ユーザープロファイルが見つかりません。",
        billwaveCustomerId: "Billwaveお客様ID",
        email: "メールアドレス",
        companyName: "企業名",
        individualContractorName: "ご契約者名",
        representativeName: "氏名",
        departmentName: "部署名",
        phoneNumber: "電話番号",
        billingInfoTitle: "請求先情報",
        billingEmailLabel: "請求先メールアドレス",
        billingEmailDesc: "BillWave利用料金の請求書やお支払いに関する通知が送信されます。",
        changeEmailButton: "メールアドレス変更",
        emailChangeRequestSuccess: "確認メールを送信しました。メール内のリンクをクリックして変更を完了してください。",
    },
    en: {
        mainTitle: "Customer Information",
        editButton: "Edit",
        cancelButton: "Cancel",
        saveButton: "Save",
        changePasswordButton: "Change Login Password",
        passwordModalTitle: "Change Password",
        newPasswordLabel: "New Password",
        passwordConfirmationLabel: "New Password (Confirm)",
        passwordMismatchError: "Passwords do not match.",
        passwordSuccessMessage: "Password changed successfully.",
        passwordErrorMessage: "Failed to change password. Please ensure it is at least 8 characters long.",
        profileUpdateSuccess: "Profile updated successfully.",
        profileUpdateError: "Failed to update profile.",
        fetchProfileError: "Failed to fetch profile.",
        notLoggedIn: "Not logged in.",
        loadingProfile: "Loading customer information...",
        profileNotFound: "User profile not found.",
        billwaveCustomerId: "Billwave Customer ID",
        email: "Login Email Address",
        companyName: "Company Name",
        individualContractorName: "Contractor Name",
        representativeName: "Name",
        departmentName: "Department Name",
        phoneNumber: "Phone Number",
        billingInfoTitle: "Billing Information",
        billingEmailLabel: "Billing Email Address",
        billingEmailDesc: "Invoices and payment notifications for BillWave usage will be sent here.",
        changeEmailButton: "Change Email Address",
        emailChangeRequestSuccess: "Verification email sent. Please click the link in the email to complete the change.",
    },
};

const CustomerInfo = () => {
    const { t, i18n } = useTranslation();
    const [userProfile, setUserProfile] = useState(null);
    const [accountInfo, setAccountInfo] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isPermissionDeniedModalOpen, setPermissionDeniedModalOpen] = useState(false);
    const [isEmailModalOpen, setEmailModalOpen] = useState(false);

    useEffect(() => {
        Object.entries(componentTranslations).forEach(([lang, resources]) => {
            i18n.addResourceBundle(lang, 'translation', resources, true, true);
        });
    }, [i18n]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [profileRes, accountRes] = await Promise.all([
                apiClient.get('/v1/user-profile'),
                apiClient.get('/v1/subscription') // This returns account data including status/plan
            ]);

            if (!profileRes.ok) throw new Error(t('fetchProfileError'));
            const profileData = await profileRes.json();
            setUserProfile(profileData);
            setFormData(profileData);

            if (accountRes.ok) {
                const accountData = await accountRes.json();
                setAccountInfo(accountData);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleEditToggle = () => {
        setIsEditing(!isEditing);
        setError('');
        setSuccess('');
        if (isEditing) {
            setFormData(userProfile);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        const { representativeName, departmentName, phoneNumber } = formData;
        
        if (!representativeName || !representativeName.trim()) {
            setError('氏名は必須項目です。');
            return;
        }

        if (representativeName.trim().length > 50) {
            setError('氏名は50文字以内で入力してください。');
            return;
        }

        if (departmentName && departmentName.trim().length > 100) {
            setError('部署名は100文字以内で入力してください。');
            return;
        }

        if (!phoneNumber || !phoneNumber.trim()) {
            setError('電話番号は必須項目です。');
            return;
        }

        if (!phoneNumber.match(/^\d{10,11}$/)) {
            setError('電話番号はハイフンなしの形式で入力してください（例: 09012345678）。');
            return;
        }

        const updateData = {
            representativeName: representativeName.trim(),
            departmentName: departmentName ? departmentName.trim() : '',
            phoneNumber: phoneNumber.trim()
        };

        try {
            const response = await apiClient.post('/v1/user-profile', updateData);
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || t('profileUpdateError'));
            
            await fetchData();
            setSuccess(result.message || t('profileUpdateSuccess'));
            setIsEditing(false);

        } catch (err) {
            setError(err.message);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmPassword) {
            setError(t('passwordMismatchError'));
            return;
        }
        if (newPassword.length < 8) {
            setError(t('passwordErrorMessage'));
            return;
        }

        try {
            const response = await apiClient.post('/v1/user-password', { newPassword });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || t('passwordErrorMessage'));
            setSuccess(result.message || t('passwordSuccessMessage'));
            setPasswordModalOpen(false);
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(err.message);
        }
    };

    const renderField = (label, value, name, editable = true, required = false) => {
        return (
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-500">
                    {label} {isEditing && editable && required && <span className="text-red-500">*</span>}
                </label>
                {isEditing && editable ? (
                    <input
                        type={name === 'phoneNumber' ? 'tel' : 'text'}
                        name={name}
                        value={formData[name] || ''}
                        onChange={handleChange}
                        required={required}
                        pattern={name === 'phoneNumber' ? "\\d{10,11}" : undefined}
                        title={name === 'phoneNumber' ? "電話番号はハイフンなしで入力してください。" : undefined}
                        placeholder={name === 'phoneNumber' ? "例: 09012345678" : undefined}
                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                ) : (
                    <p className="text-lg text-gray-900 break-words">{value || '-'}</p>
                )}
            </div>
        );
    };

    if (loading) return <div className="text-center p-8">{t('loadingProfile')}</div>;
    if (error && !userProfile) return <div className="text-center p-8 text-red-500">{error}</div>;
    if (!userProfile) return <div className="text-center p-8">{t('profileNotFound')}</div>;

    const canEdit = isEditing;
    const isAdmin = userProfile?.role === 'admin';
    const permissions = userProfile?.permissions || {};
    const canViewPaymentMethods = isAdmin || permissions.customerInfo?.canViewPaymentMethods;
    const canUpdatePaymentMethods = isAdmin || permissions.customerInfo?.canUpdatePaymentMethods;
    const canViewCustomerInfo = isAdmin || permissions.customerInfo?.canViewCustomerInfo;
    const canUpdateCustomerInfo = isAdmin || permissions.customerInfo?.canUpdateCustomerInfo;

    // Billing email logic: use billingEmail if it exists, otherwise fallback to the account email
    const currentBillingEmail = accountInfo?.billingEmail || userProfile.email;

    const isIndividual = (userProfile?.registrationType === 'individual') || 
      (accountInfo?.registrationType === 'individual') || 
      (userProfile?.billwaveCustomerId?.startsWith('2'));

    const companyOrContractorLabel = isIndividual 
      ? t('individualContractorName', 'ご契約者名') 
      : t('companyName', '企業名');

    return (
        <>
            <PermissionDeniedModal isOpen={isPermissionDeniedModalOpen} onClose={() => setPermissionDeniedModalOpen(false)} />
            <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
                <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
                    <h1 className="text-3xl font-bold text-gray-900">{t('mainTitle')}</h1>
                    <div className="flex gap-2">
                        <button onClick={() => { setPasswordModalOpen(true); setError(''); setSuccess(''); }} className="px-4 py-2 text-sm font-medium rounded-md bg-gray-600 text-white hover:bg-gray-700">
                            {t('changePasswordButton')}
                        </button>
                        <button onClick={handleEditToggle} className={`px-4 py-2 text-sm font-medium rounded-md ${isEditing ? 'bg-gray-300 hover:bg-gray-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                            {isEditing ? t('cancelButton') : t('editButton')}
                        </button>
                    </div>
                </div>

                {error && <p className="text-red-500 mb-4 bg-red-100 p-3 rounded-md">{error}</p>}
                {success && <p className="text-green-500 mb-4 bg-green-100 p-3 rounded-md">{success}</p>}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                        {/* Left Column */}
                        <div>
                            {renderField(t('billwaveCustomerId'), userProfile.billwaveCustomerId, 'billwaveCustomerId', false)}
                            {renderField(companyOrContractorLabel, userProfile.companyName, 'companyName', false)}
                            {renderField(t('departmentName'), userProfile.departmentName, 'departmentName', canEdit)}
                        </div>
                        {/* Right Column */}
                        <div>
                            {renderField(t('email'), userProfile.email, 'email', false)}
                            {renderField(t('representativeName'), userProfile.representativeName, 'representativeName', canEdit, true)}
                            {renderField(t('phoneNumber'), userProfile.phoneNumber, 'phoneNumber', canEdit, true)}
                        </div>
                    </div>

                    {isEditing && (
                        <div className="mt-6 flex justify-end">
                            <button type="submit" className="px-6 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700">
                                {t('saveButton')}
                            </button>
                        </div>
                    )}
                </form>
            </div>

            {/* Notification Settings Section */}
            <NotificationSettings />

            {/* Billing Information Card */}
            {canViewCustomerInfo && (
                <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
                    <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-900">{t('billingInfoTitle')}</h2>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-500">{t('billingEmailLabel')}</label>
                        <p className="text-lg text-gray-900">{currentBillingEmail}</p>
                        <p className="mt-1 text-sm text-gray-500">{t('billingEmailDesc')}</p>
                    </div>
                    <div className="mt-6">
                        <button
                            onClick={() => {
                                if (canUpdateCustomerInfo) {
                                    setEmailModalOpen(true);
                                } else {
                                    setPermissionDeniedModalOpen(true);
                                }
                            }}
                            className={`px-4 py-2 font-medium rounded-md text-sm ${canUpdateCustomerInfo ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                        >
                            {t('changeEmailButton')}
                        </button>
                    </div>
                </div>
            )}

            {/* Payment Methods Section */}
            {canViewPaymentMethods && (
                <PaymentMethods 
                    canUpdatePaymentMethods={canUpdatePaymentMethods} 
                    onPermissionDenied={() => setPermissionDeniedModalOpen(true)} 
                />
            )}

            <EmailChangeModal
                isOpen={isEmailModalOpen}
                onClose={() => setEmailModalOpen(false)}
                currentEmail={currentBillingEmail}
                onEmailChangeRequested={() => setSuccess(t('emailChangeRequestSuccess'))}
            />

            {isPasswordModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
                        <form onSubmit={handlePasswordChange}>
                            <h2 className="text-xl font-bold mb-4">{t('passwordModalTitle')}</h2>
                            {error && <p className="text-red-500 mb-4 bg-red-100 p-3 rounded-md">{error}</p>}
                            <div className="mb-4">
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">{t('newPasswordLabel')}</label>
                                <input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="8文字以上"
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                    autoFocus
                                />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">{t('passwordConfirmationLabel')}</label>
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="もう一度入力"
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button type="button" onClick={() => { setPasswordModalOpen(false); setError(''); }} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 font-medium rounded-md">
                                    {t('cancelButton')}
                                </button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 font-medium rounded-md">
                                    {t('saveButton')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default CustomerInfo;
